import React, { useState, useEffect, useRef } from "react";
import {
  FiChevronDown,
  FiX,
  FiCheck,
  FiFilter,
  FiPrinter,
  FiUser,
  FiUsers,
  FiSearch,
  FiRefreshCw,
} from "react-icons/fi";
import api from "../../connector/api";
import PrintableReportCard from "../components/PrintableReportCard";

const sortClassrooms = (list) => {
  const parseGradeLevel = (name = "") => {
    const lower = name.toLowerCase();
    if (lower.includes("pre") || lower.includes("nursery")) return -2;
    if (lower.includes("kinder") || lower.includes("kg")) return -1;
    const match = lower.match(/(?:grade|class|g|year)\s*(\d+)/i) || lower.match(/\b(\d+)\b/);
    if (match) return parseInt(match[1], 10);
    return 999;
  };

  return [...list].sort((a, b) => {
    const nameA = a.name || a.classroom_name || "";
    const nameB = b.name || b.classroom_name || "";
    const levelA = parseGradeLevel(nameA);
    const levelB = parseGradeLevel(nameB);

    if (levelA !== levelB) return levelA - levelB;
    return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
  });
};

export default function StudentReportCardView() {
  const [classrooms, setClassrooms] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [students, setStudents] = useState([]);

  const [selectedClassrooms, setSelectedClassrooms] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState("");
  const [selectedTerm, setSelectedTerm] = useState("");

  const [openDropdown, setOpenDropdown] = useState(null);
  const [studentSearch, setStudentSearch] = useState("");

  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [reportCards, setReportCards] = useState([]);
  const [fetchingReports, setFetchingReports] = useState(false);

  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        setLoadingFilters(true);
        const [classroomRes, yearRes, termRes] = await Promise.all([
          api.get("report/classroom/"),
          api.get("academic/years/"),
          api.get("terms/"),
        ]);

        const rawClassrooms = classroomRes.data.results || classroomRes.data || [];
        const fetchedYears = yearRes.data.results || yearRes.data || [];
        const fetchedTerms = termRes.data.results || termRes.data || [];

        setClassrooms(sortClassrooms(rawClassrooms));
        setSchoolYears(fetchedYears);
        setTerms(fetchedTerms);

        if (fetchedYears.length > 0) setSelectedSchoolYear(fetchedYears[0].id);
        if (fetchedTerms.length > 0) setSelectedTerm(fetchedTerms[0].id);
      } catch (error) {
        console.error("Failed to load filter options:", error);
      } finally {
        setLoadingFilters(false);
      }
    };

    fetchDropdownData();
  }, []);

  useEffect(() => {
    if (selectedClassrooms.length === 0) {
      setStudents([]);
      setSelectedStudents([]);
      return;
    }

    const fetchStudentsForClassrooms = async () => {
      try {
        setLoadingStudents(true);
        const classroomIds = selectedClassrooms.join(",");
        const res = await api.get(`report/students/?classrooms=${classroomIds}`);
        const fetchedStudents = res.data.results || res.data || [];
        setStudents(fetchedStudents);

        const validStudentIds = new Set(fetchedStudents.map((s) => s.id));
        setSelectedStudents((prev) => prev.filter((id) => validStudentIds.has(id)));
      } catch (error) {
        console.error("Failed to fetch students:", error);
      } finally {
        setLoadingStudents(false);
      }
    };

    fetchStudentsForClassrooms();
  }, [selectedClassrooms]);

  const toggleSelection = (id, selectedArray, setSelectedArray) => {
    if (selectedArray.includes(id)) {
      setSelectedArray(selectedArray.filter((item) => item !== id));
    } else {
      setSelectedArray([...selectedArray, id]);
    }
  };

  const handleFetchReportCards = async () => {
    if (selectedClassrooms.length === 0 || !selectedSchoolYear || !selectedTerm) return;

    try {
      setFetchingReports(true);
      const params = {
        classrooms: selectedClassrooms.join(","),
        school_year: selectedSchoolYear,
        term: selectedTerm,
      };

      if (selectedStudents.length > 0) {
        params.students = selectedStudents.join(",");
      }

      // Querying updated backend endpoint name
      const res = await api.get("report-cards/", { params });
      setReportCards(res.data.results || res.data || []);
    } catch (error) {
      console.error("Failed to fetch report cards:", error);
    } finally {
      setFetchingReports(false);
    }
  };

  const filteredStudentsList = students.filter((st) => {
    const fullName = `${st.first_name || ""} ${st.last_name || ""} ${st.full_name || ""}`.toLowerCase();
    return fullName.includes(studentSearch.toLowerCase());
  });

  return (
    <div className="space-y-6" ref={containerRef}>
      {/* Filter Control Section */}
      <div className="no-print p-4 bg-zinc-50 border border-zinc-200/80 rounded-xl space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-bold text-zinc-700 uppercase tracking-wider">
            <FiFilter className="w-4 h-4 text-zinc-500" />
            <span>Report Card Generator Filters</span>
          </div>
          {loadingFilters && (
            <span className="flex items-center gap-1.5 text-xs text-zinc-400">
              <FiRefreshCw className="w-3 h-3 animate-spin" /> Loading options...
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Classroom Selection */}
          <div className="relative">
            <label className="block text-[11px] font-semibold text-zinc-600 mb-1">
              Classrooms ({selectedClassrooms.length} selected)
            </label>
            <button
              type="button"
              disabled={loadingFilters}
              onClick={() => setOpenDropdown(openDropdown === "classroom" ? null : "classroom")}
              className="w-full flex items-center justify-between px-3 py-2 bg-white border border-zinc-200 rounded-lg text-xs text-zinc-800 hover:bg-zinc-50 focus:outline-none transition-colors disabled:opacity-50"
            >
              <span className="truncate">
                {selectedClassrooms.length === 0
                  ? "Select Classrooms..."
                  : `${selectedClassrooms.length} Classroom(s) Selected`}
              </span>
              <FiChevronDown
                className={`w-4 h-4 text-zinc-400 transition-transform ${
                  openDropdown === "classroom" ? "rotate-180" : ""
                }`}
              />
            </button>

            {openDropdown === "classroom" && (
              <div className="absolute left-0 mt-1 w-full bg-white border border-zinc-200 rounded-lg shadow-xl py-1 z-30 max-h-60 overflow-y-auto">
                <div className="px-3 py-1.5 border-b border-zinc-100 flex items-center justify-between">
                  <span className="text-[10px] font-bold text-zinc-400 uppercase">
                    Ordered Pre-K to Grade 12
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedClassrooms(
                        selectedClassrooms.length === classrooms.length
                          ? []
                          : classrooms.map((c) => c.id)
                      )
                    }
                    className="text-[10px] text-zinc-600 font-semibold hover:underline"
                  >
                    {selectedClassrooms.length === classrooms.length ? "Deselect All" : "Select All"}
                  </button>
                </div>
                {classrooms.length > 0 ? (
                  classrooms.map((cls) => {
                    const isChecked = selectedClassrooms.includes(cls.id);
                    return (
                      <button
                        key={cls.id}
                        type="button"
                        onClick={() => toggleSelection(cls.id, selectedClassrooms, setSelectedClassrooms)}
                        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-zinc-50 transition-colors text-xs"
                      >
                        <span className="text-zinc-800 font-medium truncate">
                          {cls.name || cls.classroom_name}
                        </span>
                        <div
                          className={`w-4 h-4 rounded border flex items-center justify-center ${
                            isChecked
                              ? "bg-zinc-900 border-zinc-900 text-white"
                              : "border-zinc-300 bg-white"
                          }`}
                        >
                          {isChecked && <FiCheck className="w-3 h-3" />}
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="p-3 text-center text-xs text-zinc-400">No classrooms available</div>
                )}
              </div>
            )}
          </div>

          {/* Student Selection */}
          <div className="relative">
            <label className="block text-[11px] font-semibold text-zinc-600 mb-1">
              Students (Optional - {selectedStudents.length} selected)
            </label>
            <button
              type="button"
              disabled={selectedClassrooms.length === 0 || loadingStudents}
              onClick={() => setOpenDropdown(openDropdown === "student" ? null : "student")}
              className="w-full flex items-center justify-between px-3 py-2 bg-white border border-zinc-200 rounded-lg text-xs text-zinc-800 hover:bg-zinc-50 focus:outline-none transition-colors disabled:opacity-50"
            >
              <span className="truncate">
                {selectedClassrooms.length === 0
                  ? "Select Classroom First"
                  : loadingStudents
                  ? "Loading Students..."
                  : selectedStudents.length === 0
                  ? "All Students in Classroom"
                  : `${selectedStudents.length} Student(s) Selected`}
              </span>
              <FiChevronDown
                className={`w-4 h-4 text-zinc-400 transition-transform ${
                  openDropdown === "student" ? "rotate-180" : ""
                }`}
              />
            </button>

            {openDropdown === "student" && (
              <div className="absolute left-0 mt-1 w-full bg-white border border-zinc-200 rounded-lg shadow-xl py-1 z-30 max-h-64 overflow-y-auto">
                <div className="p-2 border-b border-zinc-100">
                  <div className="flex items-center gap-2 px-2 py-1 bg-zinc-50 rounded border border-zinc-200 text-xs">
                    <FiSearch className="w-3.5 h-3.5 text-zinc-400" />
                    <input
                      type="text"
                      placeholder="Search students..."
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                      className="w-full bg-transparent focus:outline-none text-xs"
                    />
                  </div>
                </div>

                <div className="px-3 py-1 border-b border-zinc-100 flex items-center justify-between">
                  <span className="text-[10px] font-bold text-zinc-400 uppercase">Student List</span>
                  <button
                    type="button"
                    onClick={() => {
                      const allFilteredIds = filteredStudentsList.map((s) => s.id);
                      const isAllFilteredSelected = allFilteredIds.every((id) =>
                        selectedStudents.includes(id)
                      );
                      if (isAllFilteredSelected) {
                        setSelectedStudents(
                          selectedStudents.filter((id) => !allFilteredIds.includes(id))
                        );
                      } else {
                        setSelectedStudents(
                          Array.from(new Set([...selectedStudents, ...allFilteredIds]))
                        );
                      }
                    }}
                    className="text-[10px] text-zinc-600 font-semibold hover:underline"
                  >
                    {filteredStudentsList.every((s) => selectedStudents.includes(s.id)) &&
                    filteredStudentsList.length > 0
                      ? "Deselect Visible"
                      : "Select Visible"}
                  </button>
                </div>

                {filteredStudentsList.length > 0 ? (
                  filteredStudentsList.map((st) => {
                    const isChecked = selectedStudents.includes(st.id);
                    const displayName =
                      st.full_name || `${st.first_name || ""} ${st.last_name || ""}`.trim() || `Student #${st.id}`;
                    return (
                      <button
                        key={st.id}
                        type="button"
                        onClick={() => toggleSelection(st.id, selectedStudents, setSelectedStudents)}
                        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-zinc-50 transition-colors text-xs"
                      >
                        <span className="text-zinc-800 font-medium truncate">{displayName}</span>
                        <div
                          className={`w-4 h-4 rounded border flex items-center justify-center ${
                            isChecked
                              ? "bg-zinc-900 border-zinc-900 text-white"
                              : "border-zinc-300 bg-white"
                          }`}
                        >
                          {isChecked && <FiCheck className="w-3 h-3" />}
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="p-3 text-center text-xs text-zinc-400">No students found</div>
                )}
              </div>
            )}
          </div>

          {/* School Year Selection */}
          <div className="relative">
            <label className="block text-[11px] font-semibold text-zinc-600 mb-1">School Year</label>
            <button
              type="button"
              disabled={loadingFilters}
              onClick={() => setOpenDropdown(openDropdown === "year" ? null : "year")}
              className="w-full flex items-center justify-between px-3 py-2 bg-white border border-zinc-200 rounded-lg text-xs text-zinc-800 hover:bg-zinc-50 focus:outline-none transition-colors disabled:opacity-50"
            >
              <span className="truncate">
                {schoolYears.find((y) => String(y.id) === String(selectedSchoolYear))?.name ||
                  schoolYears.find((y) => String(y.id) === String(selectedSchoolYear))?.year_name ||
                  "Select School Year..."}
              </span>
              <FiChevronDown
                className={`w-4 h-4 text-zinc-400 transition-transform ${
                  openDropdown === "year" ? "rotate-180" : ""
                }`}
              />
            </button>

            {openDropdown === "year" && (
              <div className="absolute left-0 mt-1 w-full bg-white border border-zinc-200 rounded-lg shadow-xl py-1 z-30 max-h-60 overflow-y-auto">
                {schoolYears.map((yr) => (
                  <button
                    key={yr.id}
                    type="button"
                    onClick={() => {
                      setSelectedSchoolYear(yr.id);
                      setOpenDropdown(null);
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-zinc-50 transition-colors text-xs"
                  >
                    <span className="text-zinc-800 font-medium truncate">
                      {yr.name || yr.year_name}
                    </span>
                    {String(selectedSchoolYear) === String(yr.id) && (
                      <FiCheck className="w-3.5 h-3.5 text-zinc-900" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Term Selection */}
          <div className="relative">
            <label className="block text-[11px] font-semibold text-zinc-600 mb-1">Term</label>
            <button
              type="button"
              disabled={loadingFilters}
              onClick={() => setOpenDropdown(openDropdown === "term" ? null : "term")}
              className="w-full flex items-center justify-between px-3 py-2 bg-white border border-zinc-200 rounded-lg text-xs text-zinc-800 hover:bg-zinc-50 focus:outline-none transition-colors disabled:opacity-50"
            >
              <span className="truncate">
                {terms.find((t) => String(t.id) === String(selectedTerm))?.name ||
                  terms.find((t) => String(t.id) === String(selectedTerm))?.term_name ||
                  "Select Term..."}
              </span>
              <FiChevronDown
                className={`w-4 h-4 text-zinc-400 transition-transform ${
                  openDropdown === "term" ? "rotate-180" : ""
                }`}
              />
            </button>

            {openDropdown === "term" && (
              <div className="absolute left-0 mt-1 w-full bg-white border border-zinc-200 rounded-lg shadow-xl py-1 z-30 max-h-60 overflow-y-auto">
                {terms.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      setSelectedTerm(t.id);
                      setOpenDropdown(null);
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-zinc-50 transition-colors text-xs"
                  >
                    <span className="text-zinc-800 font-medium truncate">
                      {t.name || t.term_name}
                    </span>
                    {String(selectedTerm) === String(t.id) && (
                      <FiCheck className="w-3.5 h-3.5 text-zinc-900" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Selected Filter Tags */}
        {(selectedClassrooms.length > 0 || selectedStudents.length > 0) && (
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            <span className="text-[10px] font-bold text-zinc-400 uppercase mr-1">Active Selection:</span>
            {selectedClassrooms.map((id) => {
              const cls = classrooms.find((c) => c.id === id);
              return (
                <span
                  key={`cls-${id}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-zinc-200/70 text-zinc-700 text-[11px] font-medium"
                >
                  <FiUsers className="w-3 h-3 text-zinc-500" />
                  {cls?.name || cls?.classroom_name || id}
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedClassrooms(selectedClassrooms.filter((item) => item !== id))
                    }
                    className="hover:text-zinc-900"
                  >
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
              );
            })}

            {selectedStudents.map((id) => {
              const st = students.find((s) => s.id === id);
              const displayName = st
                ? st.full_name || `${st.first_name || ""} ${st.last_name || ""}`.trim()
                : id;
              return (
                <span
                  key={`st-${id}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-zinc-200/70 text-zinc-700 text-[11px] font-medium"
                >
                  <FiUser className="w-3 h-3 text-zinc-500" />
                  {displayName}
                  <button
                    type="button"
                    onClick={() => setSelectedStudents(selectedStudents.filter((item) => item !== id))}
                    className="hover:text-zinc-900"
                  >
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end pt-2 border-t border-zinc-200/60">
          <button
            type="button"
            onClick={handleFetchReportCards}
            disabled={
              selectedClassrooms.length === 0 ||
              !selectedSchoolYear ||
              !selectedTerm ||
              fetchingReports
            }
            className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white rounded-lg text-xs font-semibold hover:bg-zinc-800 disabled:opacity-40 transition-colors shadow-xs"
          >
            {fetchingReports ? (
              <FiRefreshCw className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <FiPrinter className="w-3.5 h-3.5" />
            )}
            <span>{fetchingReports ? "Loading..." : "Generate Report Cards"}</span>
          </button>
        </div>
      </div>

      {/* Rendered Cards Section */}
      {fetchingReports ? (
        <div className="py-20 text-center text-xs text-zinc-400 flex flex-col items-center gap-2">
          <FiRefreshCw className="w-5 h-5 animate-spin text-zinc-500" />
          <span>Fetching report cards...</span>
        </div>
      ) : reportCards.length > 0 ? (
        <div className="space-y-6">
          <div className="no-print p-4 border border-zinc-200 rounded-xl bg-white flex items-center justify-between">
            <p className="text-xs text-zinc-700 font-semibold">
              Loaded {reportCards.length} report card(s)
            </p>
            <button
              type="button"
              onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white rounded-lg text-xs font-semibold hover:bg-zinc-800 transition-colors"
            >
              <FiPrinter className="w-3.5 h-3.5" />
              <span>Print All Cards</span>
            </button>
          </div>

          {/* Render printable component with full list of array objects */}
          <PrintableReportCard reportCards={reportCards} />
        </div>
      ) : (
        <div className="no-print py-12 text-center text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-xl">
          Select classroom(s), year, and term above to load report cards.
        </div>
      )}
    </div>
  );
}